import { useSyncExternalStore } from 'react';
import lightThemeUrl from 'primereact/resources/themes/lara-light-green/theme.css?url';
import darkThemeUrl from 'primereact/resources/themes/lara-dark-green/theme.css?url';

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

type ThemeState = {
    preference: ThemePreference;
    resolved: ResolvedTheme;
};

const storageKey = 'vox-manage-theme';
const systemThemeQuery = '(prefers-color-scheme: dark)';
const listeners = new Set<() => void>();

let state: ThemeState = {
    preference: 'system',
    resolved: 'light'
};
let initialized = false;
let systemTheme: MediaQueryList | undefined;

/** Возвращает сохранённый режим оформления или системный режим. */
const readPreference = (): ThemePreference => {
    try {
        const saved = localStorage.getItem(storageKey);

        return saved === 'light' || saved === 'dark'
            ? saved
            : 'system';
    } catch {
        return 'system';
    }
};

/** Применяет режим оформления к странице и компонентам PrimeReact. */
const applyTheme = (): void => {
    const resolved = state.preference === 'system'
        ? (systemTheme?.matches
            ? 'dark'
            : 'light')
        : state.preference;

    state = {
        ...state,
        resolved
    };
    document.documentElement.dataset.theme = resolved;

    let stylesheet = document.getElementById('vox-prime-theme') as HTMLLinkElement | null;

    if (!stylesheet) {
        stylesheet = document.createElement('link');
        stylesheet.id = 'vox-prime-theme';
        stylesheet.rel = 'stylesheet';
        document.head.prepend(stylesheet);
    }
    stylesheet.href = resolved === 'dark'
        ? darkThemeUrl
        : lightThemeUrl;
    listeners.forEach((listener) => listener());
};

/** Запускает оформление страницы с учётом сохранённого выбора и темы системы. */
export const initializeTheme = (): void => {
    if (initialized) return;
    initialized = true;
    state = {
        ...state,
        preference: readPreference()
    };

    if (typeof window.matchMedia === 'function') {
        systemTheme = window.matchMedia(systemThemeQuery);

        systemTheme.addEventListener('change', () => {
            if (state.preference === 'system') applyTheme();
        });
    }

    applyTheme();
};

/** Сохраняет выбранный пользователем режим оформления. */
export const setThemePreference = (preference: ThemePreference): void => {
    initializeTheme();
    try {
        if (preference === 'system') localStorage.removeItem(storageKey);
        else localStorage.setItem(storageKey, preference);
    } catch {
        // Оформление остаётся доступным, даже если браузер запретил хранилище.
    }
    state = {
        ...state,
        preference
    };
    applyTheme();
};

/** Возвращает текущий выбор пользователя и фактически применённую тему. */
export const useTheme = (): ThemeState => useSyncExternalStore(
    (listener) => {
        listeners.add(listener);

        return () => listeners.delete(listener);
    },
    () => state,
    () => state,
);
